import React, { useMemo, useState } from "react";
import { Card, CardBody, CardHeader } from "reactstrap";
import * as moment from "moment";
import SimpleBar from "simplebar-react";

import PSEARatingBadge, { ratingTone } from "./PSEARatingBadge";
import PSEAHistoryEntryModal from "./PSEAHistoryEntryModal";

/**
 * Chronological log of everything that has happened to a PSEA assessment:
 * when it was first recorded, every subsequent update, the overall rating and
 * assessing agency at that point, and why the change was made.
 */

const entryTime = (entry: any) =>
  new Date(entry?.dateOfUpdate || entry?.createdAt || 0).getTime();

const isInitialEntry = (entry: any) =>
  (entry?.description || "").toLowerCase().includes("initial");

const enteredBy = (entry: any) =>
  [entry?.createdby?.firstName, entry?.createdby?.lastName]
    .filter(Boolean)
    .join(" ");

const HistoryEntry = ({
  entry,
  isLatest,
  onOpen,
}: {
  entry: any;
  isLatest: boolean;
  onOpen: () => void;
}) => {
  const initial = isInitialEntry(entry);
  const rating = entry?.overallRating;
  const tone = initial ? "primary" : ratingTone(rating);
  const author = enteredBy(entry);
  const label = entry?.description || "Assessment updated";

  return (
    <div className="acitivity-item d-flex">
      <div className="flex-shrink-0 avatar-xs acitivity-avatar">
        <div
          className={`avatar-title bg-${tone}-subtle text-${tone} rounded-circle`}
        >
          <i className={initial ? "ri-flag-2-line" : "ri-refresh-line"}></i>
        </div>
      </div>
      <div className="flex-grow-1 ms-3 pb-4">
        {/*
          A div rather than a button: the entry contains a heading and block
          content, which a button may not wrap. role/tabIndex/onKeyDown give it
          the same keyboard behaviour.
        */}
        <div
          role="button"
          tabIndex={0}
          onClick={onOpen}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onOpen();
            }
          }}
          aria-label={`View full details of history entry: ${label}`}
          className="psea-history-entry rounded p-2"
        >
          <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
            <h6 className="mb-0 lh-base">{label}</h6>
            {isLatest ? (
              <span className="badge bg-success-subtle text-success fs-11">
                Current
              </span>
            ) : null}
            <i className="ri-arrow-right-s-line text-muted ms-auto fs-16 psea-history-entry__chevron"></i>
          </div>

          <small className="text-muted d-block mb-2">
            <i className="ri-time-line align-bottom me-1"></i>
            {entry?.dateOfUpdate
              ? moment(new Date(entry.dateOfUpdate)).format(
                  "DD MMM YYYY, HH:mm"
                )
              : "-"}
            {author ? ` · by ${author}` : ""}
          </small>

          <div className="mb-2">
            <PSEARatingBadge rating={rating} />
          </div>

          <div className="text-muted fs-12 d-flex flex-wrap align-items-center gap-1 mb-2">
            <span>
              <i className="ri-government-line align-bottom me-1"></i>
              {entry?.agency?.name || "-"}
            </span>
            <span className="opacity-50">·</span>
            <span>
              <i className="ri-calendar-line align-bottom me-1"></i>
              Assessed{" "}
              {entry?.dateOfAssessment
                ? moment(new Date(entry.dateOfAssessment)).format("DD MMM YYYY")
                : "-"}
            </span>
          </div>

          {entry?.justification ? (
            <div className="d-flex gap-2 p-2 rounded bg-light border-start border-3 border-primary">
              <i className="ri-chat-quote-line text-primary fs-16"></i>
              {/* Clamped here; the modal shows the justification in full. */}
              <div className="fs-13 text-truncate">
                <span className="fw-medium">Justification: </span>
                {entry.justification}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const PSEAAssessmentHistory = ({ history }: { history: any[] }) => {
  // Newest first, so the current state of the assessment is at the top.
  const entries = useMemo(
    () => [...(history || [])].sort((a, b) => entryTime(b) - entryTime(a)),
    [history]
  );

  // Index rather than the entry itself, so "is this the current entry?" stays
  // derivable and the modal follows the entry if the list is refetched.
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const openEntry = openIndex === null ? null : entries[openIndex];

  return (
    <Card>
      <CardHeader className="d-flex align-items-center">
        <h5 className="card-title mb-0 text-uppercase fw-semibold flex-grow-1">
          Assessment History
        </h5>
        {entries.length > 0 ? (
          <span className="badge bg-primary-subtle text-primary fs-11">
            {entries.length} {entries.length === 1 ? "entry" : "entries"}
          </span>
        ) : null}
      </CardHeader>
      <CardBody>
        {entries.length === 0 ? (
          <div className="text-center py-4">
            <div className="avatar-sm mx-auto mb-3">
              <div className="avatar-title bg-light text-muted rounded-circle fs-20">
                <i className="ri-history-line"></i>
              </div>
            </div>
            <h6 className="mb-1">No history recorded yet</h6>
            <p className="text-muted mb-0 fs-13">
              Changes to this assessment will be logged here.
            </p>
          </div>
        ) : (
          <SimpleBar style={{ maxHeight: "520px" }} className="pe-2">
            <div className="acitivity-timeline acitivity-main">
              {entries.map((entry: any, index: number) => (
                <HistoryEntry
                  key={entry?.id ?? index}
                  entry={entry}
                  isLatest={index === 0}
                  onOpen={() => setOpenIndex(index)}
                />
              ))}
            </div>
          </SimpleBar>
        )}
      </CardBody>

      <PSEAHistoryEntryModal
        entry={openEntry}
        isOpen={openEntry !== null}
        toggle={() => setOpenIndex(null)}
        isLatest={openIndex === 0}
      />
    </Card>
  );
};

export default PSEAAssessmentHistory;
